import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/useSettings";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Send, ExternalLink, Lock, Users, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const TIER_ORDER = { free: 0, workshop: 1, course: 2, membership: 3, cohort: 4 };

export default function Community() {
  const { user } = useAuth();
  const { settings } = useSettings();

  const minTier = settings.telegram_min_tier || "workshop";
  const hasAccess = (TIER_ORDER[user?.tier] ?? 0) >= (TIER_ORDER[minTier] ?? 0);
  const telegramConfigured = !!settings.telegram_group_url || !!settings.telegram_channel_username;

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">Community</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">The inner circle.</h1>
          <p className="text-white/55 mt-2">Your Dr Thrive 365 tribe — ask, share, encourage.</p>
        </div>

        <Tabs defaultValue="telegram">
          <TabsList className="bg-[#0A0A0A] border border-white/5">
            <TabsTrigger value="telegram" data-testid="community-tab-telegram">
              <Send size={13} className="mr-2 text-[#229ED9]" /> Telegram
            </TabsTrigger>
            <TabsTrigger value="app" data-testid="community-tab-app">
              <MessageCircle size={13} className="mr-2" /> In-app chat
            </TabsTrigger>
          </TabsList>

          <TabsContent value="telegram" className="mt-6">
            {!hasAccess ? (
              <TelegramGated minTier={minTier} />
            ) : !telegramConfigured ? (
              <TelegramNotConfigured isAdmin={user?.is_admin} />
            ) : (
              <TelegramPanel settings={settings} />
            )}
          </TabsContent>

          <TabsContent value="app" className="mt-6">
            <InAppChat user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

/* ------------------ Telegram panel ------------------ */
function TelegramPanel({ settings }) {
  const username = (settings.telegram_channel_username || "").replace(/^@/, "");
  const groupUrl = settings.telegram_group_url;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      {/* Left: hero + CTA */}
      <div className="lg:col-span-2 space-y-4">
        <div className="glass rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#229ED9]/25 blur-3xl" />
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-[#229ED9] flex items-center justify-center ring-glow">
              <Send size={22} />
            </div>
            <h2 className="font-display text-3xl mt-5">Join the group on Telegram.</h2>
            <p className="text-sm text-white/60 mt-2 leading-relaxed">
              Real-time conversation with Dr. Slava and the community. Instant notifications on your phone. Voice notes, photos, and daily protocol wins from the tribe.
            </p>
            {groupUrl ? (
              <Button asChild data-testid="open-telegram-btn"
                className="mt-6 rounded-full w-full py-6 text-base bg-[#229ED9] hover:bg-[#1c8bbf] text-white">
                <a href={groupUrl} target="_blank" rel="noreferrer">
                  Open in Telegram <ExternalLink className="ml-2" size={15} />
                </a>
              </Button>
            ) : (
              <div className="mt-6 text-xs text-white/40 text-center py-4 rounded-lg bg-[#0F0F0F] border border-white/5">
                Group link not configured yet.
              </div>
            )}
            <p className="text-[11px] text-white/40 mt-3 text-center">
              Works on iOS, Android, desktop, and web — one tap and you're in.
            </p>
          </div>
        </div>

        <div className="glass rounded-2xl p-5 text-xs text-white/50 space-y-2">
          <div className="flex items-center gap-2 text-[#FF8A00] text-[11px] tracking-[0.22em] uppercase font-semibold">
            <Users size={12} /> Community guidelines
          </div>
          <p>Be kind. Share your wins. Ask real questions. No medical advice — only education. Dr. Slava reads everything.</p>
        </div>
      </div>

      {/* Right: embedded widget */}
      <div className="lg:col-span-3">
        {username ? (
          <div className="glass rounded-2xl p-6">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-4">Live from the channel</div>
            <TelegramDiscussionWidget
              channel={username}
              anchor={settings.telegram_discussion_post}
            />
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-sm">
              <MessageCircle size={32} className="mx-auto text-white/30" />
              <p className="text-sm text-white/55 mt-4">
                A live preview of the channel will appear here once a public channel username is configured.
                For now, tap <span className="text-[#229ED9] font-medium">Open in Telegram</span> to join the conversation.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Injects Telegram's official widget script. Uses `?embed=1` on the anchor
 * post to render an inline comments feed, or falls back to a channel-only view.
 */
function TelegramDiscussionWidget({ channel, anchor }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = "";
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    // Prefer discussion widget when an anchor post is set, otherwise post widget on channel.
    if (anchor && anchor.includes("/")) {
      script.setAttribute("data-telegram-discussion", anchor);
      script.setAttribute("data-comments-limit", "5");
      script.setAttribute("data-height", "560");
      script.setAttribute("data-colorful", "1");
      script.setAttribute("data-dark", "1");
    } else {
      // No specific post — link to the channel with a "View in Telegram" widget.
      script.setAttribute("data-telegram-post", `${channel}/1`);
      script.setAttribute("data-width", "100%");
      script.setAttribute("data-dark", "1");
    }
    // Detect load failure — Telegram will silently render nothing if the channel
    // is not public or misspelled. We poll for a bit and if nothing appears,
    // show a friendly fallback.
    const el = ref.current;
    el.appendChild(script);
    const t = setTimeout(() => {
      if (el && el.querySelector("iframe") === null) setFailed(true);
    }, 4500);
    return () => {
      clearTimeout(t);
      if (el) el.innerHTML = "";
    };
  }, [channel, anchor]);

  if (failed) {
    return (
      <div className="text-sm text-white/55 leading-relaxed p-6 rounded-lg bg-[#0F0F0F] border border-white/5">
        Couldn't load the embedded feed. This usually means the channel
        <span className="text-white/80"> @{channel}</span> isn't public yet, or the discussion post ID doesn't exist.
        The <span className="text-[#229ED9]">Open in Telegram</span> button on the left still works.
      </div>
    );
  }

  return (
    <div ref={ref} data-testid="telegram-widget-container"
      className="min-h-[400px] rounded-lg overflow-hidden" />
  );
}

function TelegramGated({ minTier }) {
  const tierLabel = { workshop: "Workshop", course: "Course", membership: "Membership", cohort: "Cohort" }[minTier] || minTier;
  return (
    <div className="glass rounded-2xl p-10 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#229ED9]/15 flex items-center justify-center">
        <Lock size={24} className="text-[#229ED9]" />
      </div>
      <h2 className="font-display text-3xl mt-5">Members only.</h2>
      <p className="text-white/55 mt-3">
        The Telegram community is reserved for {tierLabel} members and above. Join the tribe to unlock realtime chat with Dr. Slava and the group.
      </p>
      <Button asChild className="mt-6 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
        <Link to="/pricing">Unlock membership</Link>
      </Button>
    </div>
  );
}

function TelegramNotConfigured({ isAdmin }) {
  return (
    <div className="glass rounded-2xl p-10 text-center max-w-xl mx-auto">
      <Send size={32} className="mx-auto text-[#229ED9]" />
      <h2 className="font-display text-2xl mt-4">Telegram launching soon.</h2>
      <p className="text-white/55 mt-3 text-sm">
        Dr. Slava is setting up the private group. Check back shortly.
      </p>
      {isAdmin && (
        <Button asChild className="mt-5 rounded-full bg-white/10 hover:bg-white/15 border border-white/10">
          <Link to="/admin">Configure in Admin → Settings</Link>
        </Button>
      )}
    </div>
  );
}

/* ------------------ In-app chat ------------------ */
function InAppChat({ user }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/community/messages");
      setMessages(data);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const payload = text.trim();
    setText("");
    try {
      await api.post("/community/messages", { text: payload });
      load();
    } catch {}
  };

  return (
    <div className="glass rounded-2xl flex flex-col h-[65vh]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-scroll">
        {messages.map((m) => {
          const mine = m.user_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center text-xs font-semibold shrink-0">
                {m.user_name?.[0]?.toUpperCase() || "M"}
              </div>
              <div className={`max-w-[75%] ${mine ? "text-right" : ""}`}>
                <div className="text-[11px] text-white/40">
                  <span className="text-white/70">{m.user_name}</span>
                  <span className="ml-2">· {m.tier}</span>
                </div>
                <div className={`mt-1 inline-block px-4 py-2.5 rounded-2xl text-sm ${
                  mine ? "bg-gradient-to-br from-[#FF8A00]/40 to-[#E52E71]/20 rounded-tr-sm"
                       : "bg-[#0F0F0F] border border-white/5 rounded-tl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            </div>
          );
        })}
        {messages.length === 0 && (
          <p className="text-center text-sm text-white/35 mt-10">No messages yet. Be the first to say hi.</p>
        )}
      </div>
      <form onSubmit={send} className="border-t border-white/5 p-4 flex gap-3" data-testid="chat-form">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Share with the tribe…"
          data-testid="chat-input"
          className="flex-1 bg-[#0F0F0F] border border-white/5 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#FF8A00]/40"
        />
        <button type="submit" data-testid="chat-send"
          className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center hover:opacity-90">
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
